<template>
  <div class="d-flex w-100 flex-column justify-content-center">
    <div class="verify-code-container">
      <div v-if="regEmail">
        <h2 class="text-center">
          {{ $$t('Verify your email') }}
        </h2>
      </div>
      <div v-else-if="regPhone">
        <p class="mb-0">
          {{
            $$t(
              'Please, enter confirmation code here. We have sent SMS to your phone:',
              'footer-text-phone'
            )
          }}
        </p>
        <p class="text-light font-weight-bold font-size-pre-lg">
          +{{ regPhone }}
        </p>
      </div>
      <ResendCode
        v-if="resendCodeBind"
        v-bind="resendCodeBind"
        :action="sendCode"
        :action-data="templateRegData"
        @resended="refreshCode"
      />
      <div v-if="isMetaco">
        <div v-if="countdown === 0" class="text-center metaco-resend">
          {{ $$t('Didn`t receive the code? ', 'web_metaco_resend_text') }}
          <span class="resend-button" @click="resendCodeMetaco">
            {{ $$t('Resend ', 'web_metaco_resend_code_text') }}
          </span>
        </div>
        <div v-if="!canResend">
          <p class="text-center">
            {{
              $$t('The OTP code is valid til', '_web_metaco_resend_valid-til')
            }}
            {{ formattedCountdown }} <br />
            {{
              $$t(
                'Please do not share this code with anyone, including parties claiming to be Metaco & Playhera. ',
                '_web_metaco_resend_code_not_share'
              )
            }}
          </p>
        </div>
      </div>

      <InputVerify
        v-model="code"
        class=" "
        tag="form"
        auto-send
        :count="$options.verifyCodeCount"
        @onSubmit="formSubmit"
        @error="formError"
      />
      <div class="text-danger text-center">
        {{ errorRequest || errorText }}
      </div>
    </div>

    <ModalBackdrop v-model="isLoading" class="verify-code-loader">
      <Loader />
    </ModalBackdrop>
    <div></div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex';
import { translate } from '@/mixins/translate';
import isLoading from '@/mixins/isLoading';
import InputVerify from '@/components/form/InputVerify';
import Loader from '@/components/common/Loader';
import ModalBackdrop from '@/components/common/ModalBackdrop';
import ResendCode from '@/components/text/ResendCode';

let routerNameTo;

export default {
  name: 'Verification',
  components: {
    ResendCode,
    ModalBackdrop,
    Loader,
    InputVerify,
  },
  inject: ['$validator'],
  mixins: [isLoading, translate('_web_verify-code')],
  data() {
    return {
      code: '',
      errorRequest: null,
      errorText: null,
      resendTimer: 0,
      intervalTimer: 0,
      sectionWidth: 0,
      countdown: 0,
      intervalId: null,
    };
  },
  computed: {
    ...mapGetters(['templateRegData']),
    regEmail() {
      return this.$lodash.get(this, 'templateRegData.email') || '';
    },
    regPhone() {
      return this.$lodash.get(this, 'templateRegData.phone') || '';
    },
    isMetaco() {
      return this.$route.params.provider === 'metaco';
    },
    formattedCountdown() {
      const minutes = String(Math.floor(this.countdown / 60)).padStart(2, '0');
      const seconds = String(this.countdown % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    },
    canResend() {
      return this.countdown <= 0;
    },
    resendCodeBind() {
      if (this.regEmail) {
        return {
          timeoutTextTranslateKey: this.$$tCreateKey('timeout-text_email'),
          timeoutTextDefault:
            'We got your request. Re-send button will be available in {timer} sec.',
          resendTextTranslateKey: this.$$tCreateKey('retry-text_email'),
          resendTextDefault:
            'We sent you the verification code on your email. Enter the code to verify your account. Didn`t receive the code? {:resend}',
        };
      } else if (this.regPhone) {
        return {
          timeoutTextTranslateKey: this.$$tCreateKey('timeout-text_phone'),
          timeoutTextDefault:
            'We got your request. Re-send button will be available in {timer} sec.',
          resendTextTranslateKey: this.$$tCreateKey('retry-text_phone'),
          resendTextDefault: "If you didn't get SMS, try to {:resend} it.",
        };
      }
      return null;
    },
  },
  beforeRouteLeave({ name }, from, next) {
    this.unsetLoading();
    this.isLoading = false;
    routerNameTo = name;
    this.errors.clear();
    next();
  },
  created() {
    this.$options.verifyCodeCount = 4;

    if (this.isMetaco) {
      this.errorNotify({
        message: this.$t(
          '_web_metaco_otp_generated',
          'Your OTP number has been generated on metaco.gg, please take a look'
        ),
        inModal: true,
        yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
      });

      const expirationTime = this.$route.params.expiration * 1000; // expiration in ms
      const currentTime = new Date().getTime();
      const remainingTime = Math.max(
        0,
        Math.floor((expirationTime - currentTime) / 1000)
      );
      this.countdown = remainingTime;
      this.startCountdown();
    }
  },
  destroyed() {
    if (
      routerNameTo &&
      routerNameTo !== 'signup' &&
      routerNameTo !== 'passwordReset'
    ) {
      this.saveRegData(null);
    }
  },
  beforeDestroy() {
    this.unsetLoading();
    this.isLoading = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    ...mapActions([
      'verifyUser',
      'createProfile',
      'sendCode',
      'errorNotify',
      'snsAuthRequest',
      'sendMetacoCode',
      'openModal',
      'closeModal',
    ]),
    ...mapMutations(['saveRegData']),
    startCountdown() {
      this.intervalId = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.intervalId);
        }
      }, 1000);
    },
    resendCodeMetaco() {
      this.sendMetacoCode({ email: this.$route.params.email }).then(res => {
        if (res && res.data && res.data.expirationTime) {
          const currentTime = new Date().getTime();
          const remainingTime = Math.max(
            0,
            Math.floor((res.data.expirationTime * 1000 - currentTime) / 1000)
          );
          this.countdown = remainingTime;
          this.startCountdown();
        }

        this.errorNotify({
          message: this.$t(
            '_web_metaco_otp_generated',
            'Your OTP number has been generated on metaco.gg, please take a look'
          ),
          inModal: true,
        });
      });
    },
    formSubmit(code) {
      this.sectionWidth = this.$el.closest('.view-content-wrapper').offsetWidth;
      this.errorRequest = null;
      this.setLoading();

      if (this.$route.params.provider === 'metaco') {
        this.$validator.validateAll().then(() => {
          this.snsAuthRequest({
            login: this.$route.params.login,
            networkType: this.$route.params.provider,
            payload: this.code,
            email: this.$route.params.email,
          })
            .then(() => {
              if (this.$route.params.from === 'signin') {
                this.unsetLoading(); // Unset loading before navigation
                this.isLoading = false;
                this.$router.push({
                  name: 'homepage',
                });
              } else {
                this.unsetLoading(); // Unset loading before navigation
                this.$router.push({
                  name: 'profileSetup',
                  params: {
                    country: this.$route.params.country,
                  },
                });
              }
            })
            .catch(err => {
              this.code = '';
              const message =
                err.status === 403
                  ? this.$t('_web_server_errors_invalid_verification_code')
                  : err.message;
              this.errorRequest = message || err.message || err;
            })
            .finally(() => {
              this.unsetLoading();
              this.isLoading = false;
            });
        });
      } else {
        this.$validator
          .validateAll()
          .then(
            result =>
              result
                ? Promise.resolve({
                    code, // or code: this.code
                    from: this.$route.params.from || '',
                    ...this.templateRegData,
                  })
            : Promise.reject() // eslint-disable-line
          )
          .then(this.verifyUser)
          .then((this.isLoading = false))
          .then(() => {
            if (this.$route.params.from === 'signup') {
              this.createProfile().then(() => {
                this.unsetLoading(); // Unset loading before navigation
                this.isLoading = false;
                this.$router.push({
                  name: 'profileSetup',
                  query: { redirectTo: this.$route.query.redirectTo },
                });
              });
            } else {
              this.unsetLoading(); // Unset loading before navigation
              this.isLoading = false;
              this.$router.push({
                name: 'submitPassword',
                query: { redirectTo: this.$route.query.redirectTo },
              });
            }
          })
          .catch(err => {
            this.code = '';
            this.errorRequest = err.message || err;
            const from = this.$route.params.from;
            if (
              err ===
                this.$t('_web_session_expired_error', 'Session expired') &&
              from
            ) {
              this.errorNotify(err);
              this.unsetLoading(); // Unset loading before navigation
              this.isLoading = false;
              this.$nextTick(() => this.$router.push({ name: from }));
            }
          })
          .finally(() => {
            this.unsetLoading();
            this.isLoading = false;
          });
      }
    },
    formError(err) {
      this.code = '';
      this.errorText = err;
    },
    refreshCode() {
      this.code = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.verify-code-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  text-align: center;
  align-items: center;
}

.verify-code-loader {
  width: 100vw !important;
}
.resend-button {
  color: #5a58f2;
  cursor: pointer;
  &:hover {
    color: #3734bd;
  }
}

.metaco-resend {
  margin-bottom: 15px;
}
</style>
